import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

// Fullscreen QR scanner overlay. Stops the camera as soon as a code is read
// or the user closes the scanner. The parent receives the raw scanned text
// via `onScanned(text)`.
//
// html5-qrcode's `stop()` rejects with "Cannot stop, scanner is not running
// or paused" if called twice (once after the read, again on unmount). We
// guard with a `started` flag and swallow rejections defensively.

function QrScanner({ open, onClose, onScanned, title = 'Scansiona il QR' }) {
  const regionRef = useRef(null)
  const scannerRef = useRef(null)
  const fired = useRef(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return undefined
    fired.current = false
    setError('')

    let cancelled = false
    let started = false
    let stopping = null
    const elementId = 'qr-scanner-region'

    async function safeStop(inst) {
      if (!inst) return
      if (!started) return
      if (stopping) {
        // Già in fase di stop — riusa la stessa promise per evitare doppio invoke.
        try { await stopping } catch (e) { /* ignore */ }
        return
      }
      started = false
      try {
        stopping = inst.stop()
      } catch (e) {
        // stop sincrono throw: scanner già in stato invalido — ignore.
        stopping = Promise.resolve()
      }
      try { await stopping } catch (e) { /* swallow */ }
      try { inst.clear() } catch (e) { /* swallow */ }
    }

    async function start() {
      // Slight delay so the portal element is mounted before we touch it.
      await new Promise(resolve => setTimeout(resolve, 0))
      if (cancelled) return
      try {
        // Lazy-load html5-qrcode (~120KB gzipped) so it doesn't penalise the
        // initial bundle for users who never open the scanner.
        const mod = await import('html5-qrcode')
        if (cancelled) return
        const Html5Qrcode = mod.Html5Qrcode
        const inst = new Html5Qrcode(elementId)
        scannerRef.current = inst
        const cameras = await Html5Qrcode.getCameras()
        if (cancelled) {
          try { inst.clear() } catch (e) { /* noop */ }
          return
        }
        if (!cameras || cameras.length === 0) {
          setError('Nessuna fotocamera trovata sul dispositivo.')
          return
        }
        // Prefer back camera on phones; fall back to the first available.
        const back =
          cameras.find(c => /back|rear|environment/i.test(c.label || '')) ||
          cameras[cameras.length - 1]
        await inst.start(
          { deviceId: { exact: back.id } },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          decodedText => {
            if (fired.current) return
            fired.current = true
            // Stop the camera before bubbling up. safeStop is idempotent so
            // the cleanup unmount call won't double-stop.
            safeStop(inst).finally(() => {
              if (!cancelled) {
                try {
                  onScanned(decodedText)
                } catch (e) {
                  /* parent error: no recovery here */
                }
              }
            })
          },
          () => {
            /* per-frame failure — ignore */
          }
        )
        if (cancelled) {
          // Component unmounted while we were starting — stop immediately.
          started = true
          await safeStop(inst)
          return
        }
        started = true
      } catch (e) {
        if (!cancelled) {
          const msg = e && e.message ? e.message : String(e)
          if (/permission/i.test(msg) || /denied/i.test(msg)) {
            setError(
              'Permesso fotocamera negato. Abilita la fotocamera dalle impostazioni del browser per questo sito e riapri lo scanner.',
            )
          } else if (/secure/i.test(msg) || /https/i.test(msg)) {
            setError(
              'La fotocamera richiede una connessione sicura (HTTPS). Apri l\'app da https://app.cutcatering.it.',
            )
          } else {
            setError('Impossibile avviare la fotocamera: ' + msg)
          }
        }
      }
    }

    start()

    return () => {
      cancelled = true
      const inst = scannerRef.current
      scannerRef.current = null
      if (inst) {
        // Fire-and-forget: scope-local safeStop is idempotent and swallows.
        safeStop(inst)
      }
    }
  }, [open, onScanned])

  if (!open) return null

  return ReactDOM.createPortal(
    <div className="qr-scanner-overlay" role="dialog" aria-modal="true">
      <div className="qr-scanner-bar">
        <span className="qr-scanner-title">{title}</span>
        <button
          type="button"
          className="qr-scanner-close"
          onClick={onClose}
          aria-label="Chiudi"
        >
          ×
        </button>
      </div>
      <div className="qr-scanner-stage">
        <div className="qr-scanner-region" id="qr-scanner-region" ref={regionRef} />
      </div>
      {error ? (
        <p className="qr-scanner-error">{error}</p>
      ) : (
        <p className="qr-scanner-hint">
          Inquadra il QR code ricevuto dall'admin per accedere senza password.
        </p>
      )}
    </div>,
    document.body,
  )
}

export default QrScanner
